/**
 * Bulk-edit apply — turns the builder's rows plus the grid selection into the
 * one `bulk_edit` object the backend accepts (BE↔DS bulk-editing contract
 * v0.5.1) and dispatches it.
 *
 * Everything here is built out of the **generated** admin models, so the wire
 * casing is the generator's problem rather than this file's: `release_link` goes
 * out snake_case while `change_artists` keeps `stageName` / `featuredStatus`,
 * because each bucket follows the single-recording operation it reuses, and the
 * per-model `…ToJSON` mappers already encode that.
 *
 * The shape of the problem is different from mass edit. Mass edit holds a
 * per-recording desired value, diffs it, and fans out across three batch
 * endpoints — so its unit of failure is the *recording*. Bulk edit sends one
 * plan for the whole selection, DS runs one set-based query per action, and the
 * unit of failure is therefore the **action**: a failed `contributions` action
 * means the contributions didn't land, but says nothing about which recordings
 * (contract §5). That is a property of the wire protocol, not a gap here — the
 * result surface reports per action for exactly that reason.
 *
 * Two more consequences worth knowing before reading the code:
 *
 * - **One request, not one per recording.** Rows of the same field merge into a
 *   single bucket. Rows that contradict each other can't be expressed at all,
 *   so `bulkEditConflicts` (in `bulkEditModel.ts`) rejects them before we build.
 * - **`edge_id` is per recording.** A `release_link` needs each recording's
 *   existing recording→release edge, which the plan itself can't carry — hence
 *   the bulk release lookup below.
 */

import {
  BulkLookupReleaseRequestSourceEnum,
  type BulkEditRequestBulkEdit,
  type BulkEditRequestBulkEditChangeArtistsArtistChangeInner,
  type BulkEditRequestBulkEditChangeArtistsArtistLinkInner,
  type BulkEditRequestBulkEditContributionsInner,
  type BulkEditRequestBulkEditCorrectInner,
  type BulkEditRequestBulkEditRecordingsInner,
  type BulkEditRequestBulkEditReleaseLink,
  type PortalApi,
} from 'shared_components/generated/admin';
import type { ArtistEntry } from './Components/ArtistEditorModal';
import type { Row } from './MasterDiscographyTypes';
import type {
  ReleaseSelection,
  ReleaseSourceKey,
} from './Components/ChangeReleaseModal';
import {
  bulkEditConflicts,
  rowIsComplete,
  type BulkEditRow,
  type ConfiguredContribution,
  type RowValue,
} from './bulkEditModel';
import { parseEvidenceNotes } from './massEditBatch';
import { friendlySaveError } from './Components/saveErrorMessage';
import {
  contributionAliasProblem,
  hasContributionAlias,
  validateRecordingBeforeRelease,
} from '../../utils/recordingValidators';

/* The generated names are the request path spelled out in full. Aliased to the
 * contract's own vocabulary so the code below reads like §3 of the contract. */
type BulkEditPlan = BulkEditRequestBulkEdit;
type BulkRecordingRef = BulkEditRequestBulkEditRecordingsInner;
type BulkCorrectEdit = BulkEditRequestBulkEditCorrectInner;
type BulkContributionEdit = BulkEditRequestBulkEditContributionsInner;
type BulkArtistChangeEdit =
  BulkEditRequestBulkEditChangeArtistsArtistChangeInner;
type BulkArtistLinkEdit = BulkEditRequestBulkEditChangeArtistsArtistLinkInner;
type BulkReleaseLink = BulkEditRequestBulkEditReleaseLink;

/** One recording in the selection the plan applies to. */
export interface BulkEditTarget {
  recordingId: string;
  documentId?: string;
}

export interface BuildBulkEditOptions {
  /** Artist display name → graph id, for the Delete / Demote rows, which are
   *  picked from a name checklist. Falls back to the name itself, matching what
   *  mass edit sends for legacy rows without ids. */
  artistIdByName?: Record<string, string>;
  /** Recording id → grid title, so per-recording rejections can be reported by
   *  name rather than id. Only `applyBulkEdits` reads it. */
  titleByRecordingId?: Record<string, string>;
}

/** What the plan couldn't express, and why. Empty means it's ready to send. */
export interface BulkEditBuildResult {
  plan: BulkEditPlan | null;
  problems: string[];
  /** The release the plan links, kept alongside because the built
   *  `release_link` drops `source`/`source_id` when linking by id — and those
   *  are exactly what the edge lookup keys on. */
  releaseSelection?: ReleaseSelection;
}

/* -------------------------------------------------------------------------- */
/* Row → bucket                                                                */
/* -------------------------------------------------------------------------- */

const valueOfKind = <K extends RowValue['kind']>(
  rows: BulkEditRow[],
  kind: K
): Extract<RowValue, { kind: K }>[] =>
  rows
    .map((r) => r.value)
    .filter((v): v is Extract<RowValue, { kind: K }> => v?.kind === kind);

/** The generator types `correct.value` as `object` because the schema leaves it
 *  free-form — every bulk-editable field actually carries a string. One cast
 *  here rather than one at each call site. */
const correctValue = (value: string): object => value as unknown as object;

/** Recording-level corrections (contract §3.4.1). */
function buildCorrect(rows: BulkEditRow[]): BulkCorrectEdit[] {
  const out: BulkCorrectEdit[] = [];

  valueOfKind(rows, 'title').forEach((v) =>
    out.push({
      op: 'edit',
      field: 'recordingTitle',
      value: correctValue(v.title.trim()),
    })
  );

  // Only the `live` descriptor is persisted — the boolean `is_live` is derived
  // downstream from its presence, and the bulk endpoint rejects it outright.
  valueOfKind(rows, 'live').forEach((v) =>
    out.push({
      op: 'edit',
      field: 'live',
      value: correctValue(v.descriptor.trim()),
    })
  );

  // The three recording-detail fields opened up alongside the edit form and
  // mass edit (spec §5.4). The backend checks a `yearRecorded` edit against
  // each recording's earliest release (spec §3) and reports the ones it
  // refuses in `rejected`; `computeRuleViolations` runs the same check up front.
  valueOfKind(rows, 'yearRecorded').forEach((v) =>
    out.push({
      op: 'edit',
      field: 'yearRecorded',
      value: correctValue(v.year.trim()),
    })
  );

  // The grid calls it `location`; the wire field is `countryOfRecording`.
  valueOfKind(rows, 'country').forEach((v) =>
    out.push({
      op: 'edit',
      field: 'countryOfRecording',
      value: correctValue(v.code.trim()),
    })
  );

  // `pNames` is a list: one add or delete per name, never an edit.
  rows.forEach((row) => {
    if (row.value?.kind !== 'pName') return;
    const op = row.operation === 'Delete' ? 'delete' : 'add';
    row.value.names
      .map((n) => n.trim())
      .filter((n) => n.length > 0)
      .forEach((name) =>
        out.push({ op, field: 'pNames', value: correctValue(name) })
      );
  });

  return out;
}

/** Contribution edits (contract §3.4.2). */
function buildContributions(
  rows: BulkEditRow[],
  problems: string[]
): BulkContributionEdit[] {
  const out: BulkContributionEdit[] = [];

  const toEdit = (
    c: ConfiguredContribution,
    op: 'add' | 'edit' | 'replace'
  ): BulkContributionEdit => {
    const { evidenceUrl, notes } = parseEvidenceNotes(
      c.confidencePattern ?? ''
    );
    return {
      op,
      role: c.contribution,
      personaId: c.personaId,
      dataCategory: c.confidenceCategory || '1',
      sourceUrl: evidenceUrl || undefined,
      notes: notes || undefined,
    };
  };

  rows.forEach((row) => {
    if (row.value?.kind !== 'contributions') return;
    // `Replace` supersedes the recording's whole contribution set; `Add` leaves
    // the rest alone; `Edit` changes the detail of a role already there without
    // adding one (contract §6).
    const op =
      row.operation === 'Replace'
        ? 'replace'
        : row.operation === 'Edit'
        ? 'edit'
        : 'add';
    row.value.entries.forEach((entry) => {
      // Every writing op needs an alias, `edit` included: the backend rewrites
      // the contribution edge with `source_id = persona_id` on an edit too, so
      // one sent without an alias writes an edge with an empty source rather
      // than failing. Name the row instead of letting that through.
      if (!hasContributionAlias(entry.personaId)) {
        problems.push(contributionAliasProblem(entry.contribution));
        return;
      }
      out.push(toEdit(entry, op));
    });
  });

  valueOfKind(rows, 'contributionDelete').forEach((v) =>
    v.roles.forEach((role) => out.push({ op: 'delete', role }))
  );

  return out;
}

/** Map the editor's featured status onto the contract's. The editor's `nf`
 *  marker has no add-time equivalent, so it lands on "other featured" — the
 *  same mapping mass edit uses. */
const featuredFor = (entry: ArtistEntry): 'fa' | 'ofa' =>
  entry.featuredStatus === 'fa' ? 'fa' : 'ofa';

/** The `change_artists` bucket (contract §3.4.3): existing artists are linked
 *  by id, brand-new ones are created from a source id first. */
function buildChangeArtists(
  rows: BulkEditRow[],
  options: BuildBulkEditOptions
): {
  artistChange: BulkArtistChangeEdit[];
  artistLink: BulkArtistLinkEdit[];
} {
  const artistChange: BulkArtistChangeEdit[] = [];
  const artistLink: BulkArtistLinkEdit[] = [];
  const idFor = (name: string) => options.artistIdByName?.[name] || name;

  rows.forEach((row) => {
    if (row.value?.kind !== 'artist') return;
    const op = row.operation === 'Replace' ? 'replace' : 'add';
    row.value.entries.forEach((entry) => {
      if (entry.pendingCreate) {
        artistLink.push({
          source: entry.pendingCreate.source,
          sourceId: entry.pendingCreate.sourceId,
          stageName: entry.name,
          // `_exists` is the generator's rename of the wire's `exists` (it
          // avoids the reserved word); the mapper writes it back out as
          // `exists`. Same spelling mass edit uses.
          members: entry.members?.map((m) => ({
            id: m.id,
            stageName: m.stageName,
            _exists: m.exists ?? false,
          })),
          featuredStatus: featuredFor(entry),
          op,
        });
        return;
      }
      artistChange.push({
        artistId: entry.id || entry.name,
        stageName: entry.name,
        members: entry.members?.map((m) => ({
          id: m.id,
          stageName: m.stageName,
          _exists: m.exists ?? true,
        })),
        type: entry.type,
        op,
        featuredStatus: featuredFor(entry),
      });
    });
  });

  // Demote keeps the artist linked but non-featured; delete removes the link
  // outright. Both are `op: delete` — `featuredStatus` is what separates them.
  // Omitting it is how "remove" is expressed: the backend reads an absent key
  // and an explicit null identically (`data.get("featuredStatus")`).
  valueOfKind(rows, 'artistDemote').forEach((v) =>
    v.artists.forEach((name) =>
      artistChange.push({
        artistId: idFor(name),
        stageName: name,
        op: 'delete',
        featuredStatus: 'nfa',
      })
    )
  );
  valueOfKind(rows, 'artistDelete').forEach((v) =>
    v.artists.forEach((name) =>
      artistChange.push({
        artistId: idFor(name),
        stageName: name,
        op: 'delete',
      })
    )
  );

  return { artistChange, artistLink };
}

/** The `release_link` bucket (contract §3.4.4). The generated model spells the
 *  fields camelCase and its mapper emits the snake_case the contract requires —
 *  unlike the artist buckets, whose wire form really is camelCase. */
function releaseLinkFrom(selection: ReleaseSelection): BulkReleaseLink {
  const metadata = {
    releaseTitle: selection.releaseTitle,
    dateReleased: selection.dateReleased,
    releaseLabels: selection.releaseLabels,
    catNumbers: selection.catNumbers,
    mediaType: selection.mediaType,
    format: selection.format,
  };
  // Linking an existing release carries only its id; creating one carries the
  // source and metadata. Sending both is rejected by the backend.
  // `sourceReleaseIds` is required on both paths: `releaseId` is the WMC id,
  // and the backend no longer derives the source's own id from `sourceId`.
  if (selection.releaseId) {
    return {
      releaseId: selection.releaseId,
      sourceReleaseIds: [selection.sourceId],
      ...metadata,
    };
  }
  return {
    releaseId: null,
    source: selection.source,
    sourceId: selection.sourceId,
    sourceReleaseIds: [selection.sourceId],
    ...metadata,
  };
}

/* -------------------------------------------------------------------------- */
/* Plan assembly                                                               */
/* -------------------------------------------------------------------------- */

/**
 * Build the single `bulk_edit` the request carries.
 *
 * Returns `plan: null` with the reasons whenever the rows can't be expressed as
 * one coherent plan, so the caller can show them instead of sending something
 * the backend would reject wholesale (validation is all-or-nothing — contract
 * §3.1).
 */
export function buildBulkEditPlan(
  rows: BulkEditRow[],
  targets: BulkEditTarget[],
  options: BuildBulkEditOptions = {}
): BulkEditBuildResult {
  const problems = bulkEditConflicts(rows);
  const ready = rows.filter(rowIsComplete);

  if (targets.length === 0) {
    problems.push('No recordings are selected.');
  }
  if (ready.length === 0) {
    problems.push('No completed edits to apply.');
  }
  if (problems.length > 0) return { plan: null, problems };

  const recordings: BulkRecordingRef[] = targets.map((t) => ({
    recordingId: t.recordingId,
    documentId: t.documentId,
  }));

  const correct = buildCorrect(ready);
  const contributions = buildContributions(ready, problems);
  const { artistChange, artistLink } = buildChangeArtists(ready, options);
  const releaseRow = valueOfKind(ready, 'release')[0];

  if (problems.length > 0) return { plan: null, problems };

  const plan: BulkEditPlan = { recordings };
  if (correct.length > 0) plan.correct = correct;
  if (contributions.length > 0) plan.contributions = contributions;
  if (artistChange.length > 0 || artistLink.length > 0) {
    // Empty arrays are omitted: the backend rejects an all-empty bucket rather
    // than treating it as a no-op.
    plan.changeArtists = {
      ...(artistChange.length > 0 ? { artistChange } : {}),
      ...(artistLink.length > 0 ? { artistLink } : {}),
    };
  }
  if (releaseRow?.selection) {
    plan.releaseLink = releaseLinkFrom(releaseRow.selection);
  }

  const hasAction =
    !!plan.correct ||
    !!plan.contributions ||
    !!plan.changeArtists ||
    !!plan.releaseLink;
  if (!hasAction) {
    return { plan: null, problems: ['No completed edits to apply.'] };
  }

  return { plan, problems: [], releaseSelection: releaseRow?.selection };
}

/* -------------------------------------------------------------------------- */
/* Pre-warning: the recording-before-release rule                              */
/* -------------------------------------------------------------------------- */

/** One selected recording the backend will refuse under the rule (spec §3). */
export interface RuleViolation {
  recordingId: string;
  title: string;
  /** The recording's versions, joined — shown next to the title so the user
   *  can tell apart recordings that share one. Empty when there are none. */
  version: string;
  /** The year of recording the rule was checked with — the plan's new year
   *  for a Year of Recording edit, the row's own for a release change. */
  yearRecorded: string;
  /** The release date the rule was checked against — the row's earliest
   *  release for a Year of Recording edit, the chosen release's for a release
   *  change. `YYYY` or a full date, as stored. */
  releaseYear: string;
  /** Title of that release, so the line reads as a recording on a release. */
  releaseTitle: string;
  message: string;
}

/**
 * Which selected recordings the recording-before-release rule would reject,
 * evaluated from the grid's data before the plan is sent (spec §5.4).
 *
 * Two plan shapes trigger it. A Year of Recording edit is checked against each
 * recording's own earliest release date (`row.year`); a Linked Release change
 * is checked the other way round — each recording's current `yearRecorded`
 * against the chosen release's date. When a plan carries both, the backend
 * applies `correct` before `release_link`, so the year edit is what the
 * release is then compared against, and each recording is reported once.
 *
 * Rows missing either side are skipped, exactly as the backend skips them —
 * this is a preview of its verdict, not a stricter one. The plan's
 * `releaseLink.dateReleased` comes from the shared Change Release modal and is
 * `YYYY` or a full date, like `row.year`.
 *
 * Pure: takes the grid rows and the built plan, returns the offenders. The
 * modal treats any offender as a validation error: it lists them by title and
 * version and keeps Apply disabled until the year is changed or they are
 * deselected (a bulk edit is all-or-nothing from the user's point of view).
 */
const versionLabel = (row: Row): string =>
  (row.versionsArray?.length ? row.versionsArray : [row.version ?? ''])
    .map((v) => v.trim())
    .filter(Boolean)
    .join(', ');

export function computeRuleViolations(
  rows: Row[],
  plan: BulkEditPlan | null
): RuleViolation[] {
  if (!plan) return [];
  const yearEdit = plan.correct?.find(
    (c) => c.field === 'yearRecorded' && c.op === 'edit'
  );
  const newYear =
    typeof yearEdit?.value === 'string' ? (yearEdit.value as string) : null;
  const newRelease = plan.releaseLink?.dateReleased ?? null;
  if (newYear === null && newRelease === null) return [];

  const targeted = new Set(plan.recordings.map((r) => r.recordingId));
  const violations: RuleViolation[] = [];

  rows.forEach((row) => {
    if (!targeted.has(row.id)) return;
    // After the plan lands, the recording's year is the edited one (if any).
    const yearRecorded = newYear ?? row.yearRecorded ?? null;

    if (newRelease !== null) {
      // The release is changing: the rule is checked against the new release.
      const message = validateRecordingBeforeRelease(
        yearRecorded,
        newRelease,
        'release'
      );
      if (message) {
        violations.push({
          recordingId: row.id,
          title: row.title,
          version: versionLabel(row),
          yearRecorded: yearRecorded ?? '',
          releaseYear: newRelease,
          releaseTitle: plan.releaseLink?.releaseTitle ?? '',
          message,
        });
      }
      return;
    }

    const message = validateRecordingBeforeRelease(newYear, row.year);
    if (message) {
      violations.push({
        recordingId: row.id,
        title: row.title,
        version: versionLabel(row),
        yearRecorded: newYear ?? '',
        releaseYear: row.year ?? '',
        releaseTitle: row.release ?? '',
        message,
      });
    }
  });

  return violations;
}

/* -------------------------------------------------------------------------- */
/* Result                                                                      */
/* -------------------------------------------------------------------------- */

/** The actions the contract reports on. The generated response types `action`
 *  as a plain string, so this is the FE's own narrowing of it. */
export type BulkEditAction =
  | 'correct'
  | 'contributions'
  | 'artist_change'
  | 'artist_link'
  | 'release_link';

/** Human-readable names for the contract's actions, in application order. */
export const ACTION_LABELS: Record<BulkEditAction, string> = {
  correct: 'Recording details',
  contributions: 'Contributions',
  artist_change: 'Artists',
  artist_link: 'New artists',
  release_link: 'Linked release',
};

/** A recording the backend refused within an otherwise applied action. */
export interface BulkEditRejectedRecording {
  recordingId: string;
  /** The grid's title for it, when the caller could supply one; the id
   *  otherwise, so the user still has something to search for. */
  title: string;
  code: string;
  message: string;
}

export interface BulkEditActionOutcome {
  action: string;
  label: string;
  ok: boolean;
  /** Why it failed, in plain language. Absent when it succeeded. */
  message?: string;
  /** Recordings this action skipped because a per-recording rule refused
   *  them — today, the recording-before-release rule (spec §3). The action
   *  itself may still have succeeded for the rest of the selection. */
  rejected: BulkEditRejectedRecording[];
}

export interface BulkEditApplyResult {
  /** True only when every action succeeded. */
  ok: boolean;
  /** The contract's own verdict: all, some, or none of the actions landed. */
  status: 'succeeded' | 'partial' | 'failed';
  outcomes: BulkEditActionOutcome[];
  /** The request never produced a result — bad payload, auth, or transport.
   *  Nothing was applied. */
  fatalError?: string;
  /** Recordings the plan targeted; safe to refetch once `ok`. */
  recordingIds: string[];
  /** Every per-recording rejection across all actions, flattened for the
   *  “N recordings rejected” summary. */
  rejected: BulkEditRejectedRecording[];
}

/* -------------------------------------------------------------------------- */
/* Dispatch                                                                    */
/* -------------------------------------------------------------------------- */

/** The lookup's source vocabulary is the release-link one minus `other` — an
 *  `other` release is identified by a URL, so there is nothing to look up. */
const lookupSourceOf = (
  source: ReleaseSourceKey
): BulkLookupReleaseRequestSourceEnum | null =>
  (Object.values(BulkLookupReleaseRequestSourceEnum) as string[]).includes(
    source
  )
    ? (source as BulkLookupReleaseRequestSourceEnum)
    : null;

/**
 * Fill in each recording's existing recording→release edge.
 *
 * Needed only when linking a release that already exists: a selection routinely
 * mixes recordings already linked to it with ones that aren't, and DS repoints
 * an existing edge rather than adding a second one only if it's told which edge.
 * A release being *created* has no edges yet, so this is skipped there.
 *
 * A lookup failure is not fatal — the link still goes ahead without edge ids,
 * which is the same position the single-recording path is in when its lookup
 * returns nothing. A 404 (release not in the system) throws, and lands in the
 * same place.
 */
async function withEdgeIds(
  api: PortalApi,
  userId: string,
  plan: BulkEditPlan,
  selection?: ReleaseSelection
): Promise<BulkEditPlan> {
  if (!plan.releaseLink?.releaseId) return plan;
  if (!selection?.source || !selection.sourceId) return plan;

  const source = lookupSourceOf(selection.source);
  if (!source) return plan;

  try {
    const lookup = await api.bulkLookupBulkLookupReleaseResponse({
      userId,
      bulkLookupReleaseRequest: {
        source,
        sourceId: selection.sourceId,
        recordingIds: plan.recordings.map((r) => r.recordingId),
      },
    });

    const edgeByRecording = new Map(
      lookup.recordings.map((r) => [r.recordingId, r.edgeId ?? null])
    );
    return {
      ...plan,
      recordings: plan.recordings.map((r) => ({
        ...r,
        edgeId: edgeByRecording.get(r.recordingId) ?? null,
      })),
    };
  } catch {
    // Lookup is an optimisation, not a precondition — proceed without it.
    return plan;
  }
}

/**
 * Send the plan and translate the response into something the UI can show.
 *
 * A 200 with failed actions is *not* an exception — that's the contract's
 * partial-success case, and it comes back in `outcomes`. Only a request that
 * never produced a result sets `fatalError`, and in that case nothing was
 * applied at all (validation is all-or-nothing).
 */
export async function applyBulkEdits(
  api: PortalApi,
  userId: string,
  rows: BulkEditRow[],
  targets: BulkEditTarget[],
  options: BuildBulkEditOptions = {}
): Promise<BulkEditApplyResult> {
  const recordingIds = targets.map((t) => t.recordingId);
  const { plan, problems, releaseSelection } = buildBulkEditPlan(
    rows,
    targets,
    options
  );

  if (!plan) {
    return {
      ok: false,
      status: 'failed',
      outcomes: [],
      fatalError: problems.join(' '),
      recordingIds,
      rejected: [],
    };
  }

  try {
    const bulkEdit = await withEdgeIds(api, userId, plan, releaseSelection);
    const response = await api.bulkEditBulkEditResultResponse({
      userId,
      bulkEditRequest: {
        bulkEdit,
        eventTime: new Date().toISOString(),
      },
    });

    const titleOf = (id: string) => options.titleByRecordingId?.[id] || id;
    const outcomes: BulkEditActionOutcome[] = (response.results ?? []).map(
      (result) => ({
        action: result.action,
        label: ACTION_LABELS[result.action as BulkEditAction] ?? result.action,
        ok: result.status === 'succeeded',
        message:
          result.status === 'succeeded'
            ? undefined
            : result.error?.message ||
              'This part of the edit could not be applied.',
        rejected: (result.rejected ?? []).map((r) => ({
          recordingId: r.recordingId,
          title: titleOf(r.recordingId),
          code: r.code,
          message: r.message,
        })),
      })
    );
    const rejected = outcomes.flatMap((o) => o.rejected);

    return {
      // A per-recording rejection is a partial outcome even when every action
      // reports success: those recordings did not get the edit.
      ok: response.status === 'succeeded' && rejected.length === 0,
      status:
        response.status === 'succeeded' && rejected.length > 0
          ? 'partial'
          : response.status,
      outcomes,
      recordingIds,
      rejected,
    };
  } catch (err) {
    return {
      ok: false,
      status: 'failed',
      outcomes: [],
      fatalError: await friendlySaveError(err, 'recording'),
      recordingIds,
      rejected: [],
    };
  }
}
